import { z } from 'zod'

// ===================
// Bid Schemas
// ===================

export const BidStatus = z.enum(['lead', 'qualifying', 'drafting', 'submitted', 'won', 'lost', 'no_go'])
export type BidStatus = z.infer<typeof BidStatus>

// R4 stage model (Specs/2026-10-13-r4-shell/plan.md §4.1). Optional on the bid:
// a backend before migration 201 sends only `status`. Read them through
// `bidLifecycleOf` (`@/lib/bid-lifecycle`), which derives them when missing.
export const BidLifecycleEnum = z.enum(['lead', 'active', 'lodged', 'closed'])
export const BidStageEnum = z.enum(['qualify', 'plan', 'write', 'review', 'submit'])
export const BidOutcomeEnum = z.enum(['won', 'lost', 'no_go', 'withdrawn', 'cancelled'])

export const BidPriority = z.enum(['low', 'medium', 'high', 'critical'])
export type BidPriority = z.infer<typeof BidPriority>

export const BidScopeProfile = z.enum(['quote', 'tender', 'major_tender'])
export type BidScopeProfile = z.infer<typeof BidScopeProfile>

export const BidRoute = z.enum(['open_market', 'invited', 'panel_calloff', 'subcontract_package'])
export type BidRoute = z.infer<typeof BidRoute>

// ===================
// Bid Profile (spec 26 — "who you're writing for")
// ===================
//
// Derived on read by the backend and returned on `GET /bids/{id}` ONLY — never
// on the list. Nothing here is writable through the bid update paths; the one
// stored piece is the user's own answer, sent as `PATCH /bids/{id}`
// `{ _profile: { reader } }` / `{ _profile: { reset: ['reader'] } }`.

export const ProfileConfidence = z.enum(['high', 'medium', 'low'])
export type ProfileConfidence = z.infer<typeof ProfileConfidence>

/** Where a profile field's value came from. `none` always pairs with `unknown`. */
export const ProfileFieldSource = z.enum([
  'user',
  'analysis',
  'overview',
  'documents',
  'derived',
  'none',
])
export type ProfileFieldSource = z.infer<typeof ProfileFieldSource>

/** `reader` plus the honest third state — the card renders it as "Not known yet". */
export const ProfileReader = z.enum(['client', 'panel', 'unknown'])
export type ProfileReader = z.infer<typeof ProfileReader>

export const ProfileReaderFieldSchema = z.object({
  value: ProfileReader,
  confidence: ProfileConfidence,
  source: ProfileFieldSource,
  /** Short, user-readable reasons — the card's "Why" disclosure. */
  evidence: z.array(z.string()).default([]),
})
export type ProfileReaderField = z.infer<typeof ProfileReaderFieldSchema>

/**
 * A fact read off the Tender Summary (profile v2): buyer, location, contract
 * type, value, term, pricing model. `evidence` holds the summary's source quote,
 * or nothing when the summary stated the value without a resolvable citation.
 */
export const ProfileFactFieldSchema = z.object({
  value: z.string(),
  confidence: ProfileConfidence,
  source: ProfileFieldSource,
  evidence: z.array(z.string()).default([]),
})
export type ProfileFactField = z.infer<typeof ProfileFactFieldSchema>

/** The raw inputs the derivation saw. Carried so the card needs no second call. */
export const BidProfileSignalsSchema = z.object({
  criteriaCount: z.number(),
  weightedCriteriaCount: z.number(),
  analysis: z.object({ ranAt: z.string(), chunkCount: z.number() }).nullable(),
  overview: z
    .object({
      weightedCriteriaPresent: z.boolean(),
      returnablesPresent: z.boolean(),
      at: z.string(),
    })
    .nullable(),
  formalRoles: z.array(z.object({ role: z.string(), documentName: z.string() })).default([]),
  strongMarkers: z.array(z.string()).default([]),
  indexedDocumentCount: z.number(),
  indexingDocumentCount: z.number(),
  publicBuyerMatch: z.boolean(),
})
export type BidProfileSignals = z.infer<typeof BidProfileSignalsSchema>

/**
 * `version` exists so a later field is a type change, not a migration. v2 added
 * the Tender Summary facts; each is absent when the summary did not state it.
 */
export const BidProfileSchema = z.object({
  version: z.literal(2),
  reader: ProfileReaderFieldSchema,
  buyer: ProfileFactFieldSchema.optional(),
  location: ProfileFactFieldSchema.optional(),
  contractType: ProfileFactFieldSchema.optional(),
  value: ProfileFactFieldSchema.optional(),
  term: ProfileFactFieldSchema.optional(),
  pricingModel: ProfileFactFieldSchema.optional(),
  signals: BidProfileSignalsSchema,
})
export type BidProfile = z.infer<typeof BidProfileSchema>

export const BidSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1, 'Title is required'),
  client: z.string().min(1, 'Client is required'),
  status: BidStatus,
  lifecycle: BidLifecycleEnum.nullable().optional(),
  stage: BidStageEnum.nullable().optional(),
  outcome: BidOutcomeEnum.nullable().optional(),
  lodgedAt: z.string().datetime().nullable().optional(),
  closedAt: z.string().datetime().nullable().optional(),
  priority: BidPriority,
  value: z.number().positive().optional(),
  deadline: z.string().datetime().optional(),
  // Whether `deadline` is an exact closing time (true) or a date (false). Null
  // on bids saved before times existed, and on bids with no deadline: both read
  // as date-only. Only meaningful beside a deadline.
  deadlineHasTime: z.boolean().nullable().optional(),
  description: z.string().optional(),
  tenderSummary: z.string().optional().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  organizationId: z.string().uuid(),
  assignedTo: z.array(z.string().uuid()).default([]),
  ownerId: z.string().uuid().nullable().optional(),
  tags: z.array(z.string()).default([]),
  archived: z.boolean().default(false),
  // Missing on older/stale responses — default to 'org' so restriction is opt-in.
  visibility: z.enum(['org', 'restricted']).default('org'),
  scopeProfile: BidScopeProfile.nullable().optional(),
  scopeProfileSource: z.enum(['auto', 'user']).nullable().optional(),
  // Missing on stale responses — route is opt-in, default is the open market.
  bidRoute: BidRoute.default('open_market'),
  // Spec 24 — who reads this bid. Read-only on the frontend; written by analysis/the agent/the user via the AI panel.
  reader: z.enum(['client', 'panel']).nullable().optional(),
  readerSource: z.enum(['extraction', 'overview', 'agent_inferred', 'user']).nullable().optional(),
  // Spec 26 — the derived profile. OPTIONAL on purpose: it rides the detail
  // route only, so list rows never carry it, and a backend that predates this
  // release omits it entirely. Either way the bid page must still render.
  profile: BidProfileSchema.optional(),
  deletedAt: z.string().datetime().nullable().optional(),
  deletedBy: z.string().uuid().nullable().optional(),
  isBidMember: z.boolean().optional(),
  canEdit: z.boolean().optional(),
})
export type Bid = z.infer<typeof BidSchema>

export const CreateBidSchema = BidSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archived: true,
  visibility: true,
  scopeProfile: true,
  scopeProfileSource: true,
  bidRoute: true,
  reader: true,
  readerSource: true,
  profile: true,
  deletedAt: true,
  deletedBy: true,
  outcome: true,
  lodgedAt: true,
  closedAt: true,
})
export type CreateBid = z.infer<typeof CreateBidSchema>

// Scope fields are deliberately NOT writable here: the backend's generic bid
// update strips them, so including them would advertise a silent no-op. For
// `bidRoute` the only writable path is `_scope` / `setBidScope` (the edit bid
// dialog). `reader` has no frontend write path at all — analysis, the agent,
// or the user via the AI panel writes it server-side. `profile` is derived, so
// the only thing a client can write is the override, via `_profile`.
//
// `deadline: null` clears the deadline. The backend records that as a person's
// choice (deadline_source 'user_cleared'), so key-dates extraction never puts
// it back — send null only when a person removed a deadline the bid had.
export const UpdateBidSchema = BidSchema.omit({
  scopeProfile: true,
  scopeProfileSource: true,
  bidRoute: true,
  reader: true,
  readerSource: true,
  profile: true,
  // R4: moves go through `moveBidStage` / `moveBidLifecycle`, outcomes through
  // `_outcome`. The generic update never carries them.
  lifecycle: true,
  stage: true,
  outcome: true,
  lodgedAt: true,
  closedAt: true,
})
  .partial()
  .required({ id: true })
  .extend({ deadline: z.string().datetime().nullable().optional() })
export type UpdateBid = z.infer<typeof UpdateBidSchema>

// ===================
// User Schemas
// ===================

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string().min(1).optional(),
  avatarUrl: z.string().url().optional(),
  organizationId: z.string().uuid().optional(),
  role: z.enum(['admin', 'member', 'viewer']).default('member'),
  createdAt: z.string().datetime(),
})
export type User = z.infer<typeof UserSchema>

// ===================
// Organization Schemas
// ===================

export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Organisation name is required'),
  domain: z.string().optional(),
  logoUrl: z.string().url().optional(),
  createdAt: z.string().datetime(),
})
export type Organization = z.infer<typeof OrganizationSchema>

// ===================
// Chat/Message Schemas
// ===================

export const MessageRole = z.enum(['user', 'assistant', 'system'])
export type MessageRole = z.infer<typeof MessageRole>

export const ChatMessageSchema = z.object({
  id: z.string().uuid(),
  role: MessageRole,
  content: z.string(),
  timestamp: z.string().datetime(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})
export type ChatMessage = z.infer<typeof ChatMessageSchema>

export const ChatSessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  bidId: z.string().uuid().optional(),
  messages: z.array(ChatMessageSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type ChatSession = z.infer<typeof ChatSessionSchema>

// ===================
// Document Schemas
// ===================

export const ProcessingStatus = z.enum(['pending', 'processing', 'completed', 'failed'])
export type ProcessingStatus = z.infer<typeof ProcessingStatus>

export const IndexingStatus = z.enum(['pending', 'indexing', 'indexed', 'failed', 'skipped'])
export type IndexingStatus = z.infer<typeof IndexingStatus>

// Combined display status for a document row. One honest "working" state:
// extraction, metadata, PDF conversion, and vector indexing all render as
// 'processing' — a document is only Ready once indexing reached a terminal
// outcome ('indexed' or 'skipped'; null = legacy pre-indexing rows).
// Mirrors the aggregate banner logic in documents-store.ts
// (hasProcessingDocuments) so per-row and aggregate UI stay in sync.
export type DocumentDisplayStatus = 'pending' | 'processing' | 'completed' | 'failed'

export const DOCUMENT_DISPLAY_STATUS_CONFIG: Record<
  DocumentDisplayStatus,
  { label: string; variant: 'secondary' | 'warning' | 'success' | 'destructive' }
> = {
  pending: { label: 'Pending', variant: 'secondary' },
  processing: { label: 'Processing', variant: 'warning' },
  completed: { label: 'Ready', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' },
}

export interface DocumentStatusPair {
  processingStatus: ProcessingStatus
  indexingStatus?: IndexingStatus | null | undefined
}

export function getDocumentDisplayStatus(doc: DocumentStatusPair): DocumentDisplayStatus {
  if (doc.processingStatus === 'failed' || doc.indexingStatus === 'failed') return 'failed'
  if (doc.processingStatus !== 'completed') return doc.processingStatus
  if (doc.indexingStatus === 'pending' || doc.indexingStatus === 'indexing') return 'processing'
  return 'completed'
}

/**
 * The single definition of "ready": extraction complete AND indexing at a
 * terminal outcome ('indexed', 'skipped', or legacy null). There is no
 * window where a document with zero chunks in Weaviate counts as ready —
 * 'pending' after extraction now reads as in-flight, which is safe because
 * every pipeline code-exit persists a terminal status (spec:
 * Specs/document-status-lifecycle-spec.md).
 */
export function isDocumentReadyForAnalysis(doc: DocumentStatusPair): boolean {
  return getDocumentDisplayStatus(doc) === 'completed'
}

/**
 * Counterpart to `isDocumentReadyForAnalysis`: the document is still moving.
 * Drives the processing banner and the status poll. Terminal failures are not
 * in flight — otherwise the banner would spin forever.
 */
export function isDocumentInFlight(doc: DocumentStatusPair): boolean {
  const status = getDocumentDisplayStatus(doc)
  return status === 'pending' || status === 'processing'
}

export const DocumentCategory = z.enum([
  'rfp_tender',           // RFP / Tender Docs ("Conditions & Scope" on bid surfaces)
  'addenda',              // Addenda / Clarifications
  'site_technical',       // Site & Technical Info — bid-only (spec 25 §4.2)
  'company_knowledge',    // Company Knowledge
  'case_study',           // Case Studies
  'cv',                   // CVs
  'pricing',              // Pricing / Commercial
  'policy_certification', // Policies / Certifications
  'past_bid_submission',  // Harvested submissions (Specs/win-loss-harvest-spec.md)
  'evaluator_debrief',    // Bid-scoped evaluator debrief letters
  'other',                // Other
])
export type DocumentCategory = z.infer<typeof DocumentCategory>

// Human-readable labels for document categories
export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  rfp_tender: 'RFP / Tender Docs',
  addenda: 'Addenda / Clarifications',
  site_technical: 'Site & Technical Info',
  company_knowledge: 'Company Knowledge',
  case_study: 'Case Studies',
  cv: 'CVs',
  pricing: 'Pricing / Commercial',
  policy_certification: 'Policies / Certifications',
  past_bid_submission: 'Past Bid Submissions',
  evaluator_debrief: 'Evaluator Debriefs',
  other: 'Other',
}

/**
 * Labels on BID surfaces (the Documents tab and its filters, spec 25 §4.2).
 * Only `rfp_tender` differs: inside a tender pack it holds the conditions,
 * criteria, scope, contract and returnables, so "RFP / Tender Docs" says
 * nothing — every file there is a tender doc. The Library keeps
 * DOCUMENT_CATEGORY_LABELS.
 */
export const BID_DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  ...DOCUMENT_CATEGORY_LABELS,
  rfp_tender: 'Conditions & Scope',
}

/**
 * Categories a *library folder* may carry. Everything in DocumentCategory
 * except `rfp_tender`, `addenda` and `site_technical`, which describe a
 * document's place inside one bid's tender pack and so can never be reusable
 * library content.
 *
 * Mirrors `LibraryFolderCategory` in the backend and the
 * `library_folders_category_check` CHECK constraint (migration 123). Folder
 * pickers MUST build their options from LIBRARY_FOLDER_CATEGORY_LABELS below,
 * not from DOCUMENT_CATEGORY_LABELS — offering the bid-only two gets a 400.
 */
export const LibraryFolderCategory = z.enum([
  'company_knowledge',
  'case_study',
  'cv',
  'pricing',
  'policy_certification',
  'past_bid_submission',
  'evaluator_debrief',
  'other',
])
export type LibraryFolderCategory = z.infer<typeof LibraryFolderCategory>

/** Labels for the folder pickers. Derived, so it cannot drift from the labels above. */
export const LIBRARY_FOLDER_CATEGORY_LABELS: Record<LibraryFolderCategory, string> =
  Object.fromEntries(
    LibraryFolderCategory.options.map((c) => [c, DOCUMENT_CATEGORY_LABELS[c]])
  ) as Record<LibraryFolderCategory, string>

// Category colors for badges
export const DOCUMENT_CATEGORY_COLORS: Record<DocumentCategory, string> = {
  rfp_tender: 'bg-gray-100 text-gray-800',
  addenda: 'bg-purple-100 text-purple-800',
  site_technical: 'bg-teal-100 text-teal-800',
  company_knowledge: 'bg-green-100 text-green-800',
  case_study: 'bg-orange-100 text-orange-800',
  cv: 'bg-pink-100 text-pink-800',
  pricing: 'bg-yellow-100 text-yellow-800',
  policy_certification: 'bg-cyan-100 text-cyan-800',
  past_bid_submission: 'bg-blue-100 text-blue-800',
  evaluator_debrief: 'bg-amber-100 text-amber-800',
  other: 'bg-gray-100 text-gray-800',
}

export const DocumentSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  type: DocumentCategory,
  mimeType: z.string(),
  size: z.number().positive(),
  s3Key: z.string(),
  bidId: z.string().uuid().optional().nullable(),
  organizationId: z.string().uuid().optional().nullable(),
  uploadedBy: z.string().uuid(),
  createdAt: z.string().datetime(),
  metadata: z.record(z.string(), z.unknown()).optional().nullable(),
  // Source columns added in migration 103. Default + optional so docs created
  // before the migration ran still validate.
  source: z.enum(['upload', 'sharepoint', 'harvest']).default('upload').optional(),
  sourceMetadata: z.record(z.string(), z.unknown()).optional().nullable(),
  // Harvest provenance + AI metadata bundle (pastBid lives here)
  extractedMetadata: z.record(z.string(), z.unknown()).optional().nullable(),
  // Processing fields
  processingStatus: ProcessingStatus.default('pending'),
  extractedText: z.string().optional().nullable(),
  processingMetadata: z.record(z.string(), z.unknown()).optional().nullable(),
  processingStartedAt: z.string().datetime().optional().nullable(),
  processingCompletedAt: z.string().datetime().optional().nullable(),
  pageCount: z.number().int().positive().optional().nullable(),
  wordCount: z.number().int().nonnegative().optional().nullable(),
  // Indexing fields (Weaviate vector indexing)
  indexingStatus: IndexingStatus.optional().nullable(),
  chunkCount: z.number().int().nonnegative().optional().nullable(),
  indexedAt: z.string().datetime().optional().nullable(),
  indexingMetadata: z.record(z.string(), z.unknown()).optional().nullable(),
  // Uploader name (enriched via JOIN, only present in library list responses)
  uploaderName: z.string().optional().nullable(),
  // Manual quality tier override. 'gold' retired (Fact Bank replaces it) —
  //   'archive' → chunks treated as low_value at retrieval
  //   null      → use the heuristic classifier (default)
  qualityTier: z.literal('archive').optional().nullable(),
  qualityTierSetBy: z.string().uuid().optional().nullable(),
  qualityTierSetAt: z.string().datetime().optional().nullable(),
  // Fact Bank membership + AI-extracted metadata (Layers 1 & 2)
  inFactBank: z.boolean().optional(),
  factBankSetBy: z.string().uuid().optional().nullable(),
  factBankSetAt: z.string().datetime().optional().nullable(),
  contentClass: z.string().optional().nullable(),
  effectiveDate: z.string().optional().nullable(),
  expiryDate: z.string().optional().nullable(),
  aiSummary: z.string().optional().nullable(),
  // Versioning (library versioning). `version` is the current version number;
  // `updatedAt` tracks the last modification; `factBankSetByName` enriches the
  // Fact Bank curator (list responses only). `previousVersions` is the replaced-
  // version history snapshot, present in detail responses only (null in lists).
  version: z.number().optional(),
  updatedAt: z.string().nullable().optional(),
  factBankSetByName: z.string().nullable().optional(),
  previousVersions: z
    .array(
      z.object({
        version: z.number(),
        s3Key: z.string(),
        name: z.string(),
        size: z.number(),
        mimeType: z.string(),
        replacedAt: z.string(),
        replacedBy: z.string(),
      })
    )
    .nullable()
    .optional(),
  // Bid document list enrichment (GET /bids/{id}/documents only — pack map
  // PR3, spec 25 §5.2). Derived by the backend policy module; the frontend
  // shows them and never recomputes them. All optional: every other document
  // response omits them, and an older backend does too.
  //
  // The group the row sits under: user choice → detected role → stored type.
  effectiveCategory: DocumentCategory.optional(),
  // Set only when the row is grouped by an unconfirmed detection (the quiet
  // "Suggested" label); equals effectiveCategory when set. Null on a
  // user-chosen category, on a detected addendum, and when detection agrees
  // with the stored type.
  suggestedCategory: DocumentCategory.nullable().optional(),
  // 'user' once a person chose the category; null = upload default / legacy.
  categorySource: z.literal('user').nullable().optional(),
  readability: z.enum(['readable', 'processing', 'failed', 'no_searchable_text']).optional(),
  // Latest completed Compliance Matrix / Submission Checklist runs. Null when
  // no completed run can answer, or the lookup failed.
  analysisMembership: z.enum(['included', 'not_included', 'added_after']).nullable().optional(),
  // Addenda rows only; null elsewhere, and on an addenda row whose count
  // lookup failed.
  changedItems: z
    .object({
      pending: z.number().int().nonnegative(),
      accepted: z.number().int().nonnegative(),
      dismissed: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
})
export type Document = z.infer<typeof DocumentSchema>

// ===================
// File Type Configuration
// ===================

export interface FileTypeConfig {
  extensions: string[]
  maxSizeBytes: number
  label: string
}

export const ALLOWED_FILE_TYPES: Record<string, FileTypeConfig> = {
  // PDF
  'application/pdf': {
    extensions: ['.pdf'],
    maxSizeBytes: 100 * 1024 * 1024, // 100MB
    label: 'PDF',
  },
  // Word Documents
  'application/msword': {
    extensions: ['.doc'],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    label: 'Word Document',
  },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensions: ['.docx'],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    label: 'Word Document',
  },
  // PowerPoint
  'application/vnd.ms-powerpoint': {
    extensions: ['.ppt'],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    label: 'PowerPoint',
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    extensions: ['.pptx'],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    label: 'PowerPoint',
  },
  // Excel
  'application/vnd.ms-excel': {
    extensions: ['.xls'],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    label: 'Excel Spreadsheet',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extensions: ['.xlsx'],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    label: 'Excel Spreadsheet',
  },
  'application/vnd.ms-excel.sheet.macroEnabled.12': {
    extensions: ['.xlsm'],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    label: 'Excel Spreadsheet',
  },
  // Text files
  'text/plain': {
    extensions: ['.txt'],
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    label: 'Text File',
  },
  'text/csv': {
    extensions: ['.csv'],
    maxSizeBytes: 10 * 1024 * 1024, // 10MB
    label: 'CSV File',
  },
  // Images
  'image/png': {
    extensions: ['.png'],
    maxSizeBytes: 10 * 1024 * 1024, // 10MB
    label: 'PNG Image',
  },
  'image/jpeg': {
    extensions: ['.jpg', '.jpeg'],
    maxSizeBytes: 10 * 1024 * 1024, // 10MB
    label: 'JPEG Image',
  },
}

/**
 * Case-insensitive index over ALLOWED_FILE_TYPES.
 *
 * `File.type` is ASCII-lowercased by the platform (Blob spec), so a browser hands
 * us `application/vnd.ms-excel.sheet.macroenabled.12` while the IANA registration
 * — and SharePoint — spell it `…macroEnabled.12`. Keying lookups on the lowercase
 * form accepts both. The canonical spelling stays as the ALLOWED_FILE_TYPES key so
 * `getAcceptedFileTypes()` still emits it for `accept` attributes.
 */
const ALLOWED_FILE_TYPES_BY_LOWER: Record<string, FileTypeConfig> = Object.fromEntries(
  Object.entries(ALLOWED_FILE_TYPES).map(([mimeType, config]) => [
    mimeType.toLowerCase(),
    config,
  ])
)

/**
 * Check if a MIME type is allowed
 */
export function isAllowedMimeType(mimeType: string): boolean {
  return mimeType.toLowerCase() in ALLOWED_FILE_TYPES_BY_LOWER
}

/**
 * Get max file size for a MIME type (in bytes)
 */
export function getMaxSizeForMimeType(mimeType: string): number {
  return ALLOWED_FILE_TYPES_BY_LOWER[mimeType.toLowerCase()]?.maxSizeBytes ?? 0
}

/**
 * Get accepted file types string for input/dropzone
 */
export function getAcceptedFileTypes(): Record<string, string[]> {
  const accept: Record<string, string[]> = {}
  for (const [mimeType, config] of Object.entries(ALLOWED_FILE_TYPES)) {
    accept[mimeType] = config.extensions
  }
  return accept
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Validate file type and size
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  if (!isAllowedMimeType(file.type)) {
    return { valid: false, error: `File type '${file.type || 'unknown'}' is not supported` }
  }

  const maxSize = getMaxSizeForMimeType(file.type)
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size (${formatFileSize(file.size)}) exceeds maximum (${formatFileSize(maxSize)})`,
    }
  }

  return { valid: true }
}

/**
 * Normalize a filename for duplicate comparison.
 * Strips extension, lowercases, and normalizes separators.
 */
export function normalizeFilename(filename: string): string {
  const lastDot = filename.lastIndexOf('.')
  const baseName = lastDot > 0 ? filename.substring(0, lastDot) : filename
  return baseName
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// ===================
// Library Schemas
// ===================

export const LibraryFolderSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Folder name is required'),
  parentId: z.string().uuid().nullable(),
  category: LibraryFolderCategory,
  organizationId: z.string().uuid(),
  // Backend returns string | null (creator may be deleted / system-created).
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Filter-aware per-folder document count from GET /library/folders. Absent on
  // create/update responses, so optional.
  documentCount: z.number().optional(),
})
export type LibraryFolder = z.infer<typeof LibraryFolderSchema>

export const DocumentTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Tag name is required').max(100),
  // Color comes from the canonical palette in src/lib/tag-colors.ts.
  // Optional + nullable so legacy tags missing the column still validate.
  color: z.string().max(20).optional().nullable(),
  organizationId: z.string().uuid(),
  createdAt: z.string().datetime(),
})
export type DocumentTag = z.infer<typeof DocumentTagSchema>

// Extended document with library-specific fields
export const LibraryDocumentSchema = DocumentSchema.extend({
  ownerId: z.string().uuid().nullable().optional(),
  folderId: z.string().uuid().nullable().optional(),
  tags: z.array(DocumentTagSchema).default([]),
})
export type LibraryDocument = z.infer<typeof LibraryDocumentSchema>

// Version history (F26). `addedAt`/`addedByName` are null on the oldest retained
// entry when it isn't v1 — its predecessor was trimmed past the 5-version cap,
// so the handover was never recorded.
export const DocumentVersionSchema = z.object({
  version: z.number(),
  name: z.string(),
  size: z.number(),
  mimeType: z.string(),
  isCurrent: z.boolean(),
  addedAt: z.string().nullable(),
  addedByName: z.string().nullable(),
  supersededAt: z.string().nullable(),
  supersededByName: z.string().nullable(),
})
export type DocumentVersion = z.infer<typeof DocumentVersionSchema>

export const DocumentVersionHistorySchema = z.object({
  versions: z.array(DocumentVersionSchema),
  currentVersion: z.number(),
  /** > 1 means older versions existed but are no longer retained. */
  oldestRetainedVersion: z.number(),
})
export type DocumentVersionHistory = z.infer<typeof DocumentVersionHistorySchema>

// ===================
// Requirement Schemas
// ===================

export const RequirementType = z.enum(['mandatory', 'desirable', 'information'])
export type RequirementType = z.infer<typeof RequirementType>

export const REQUIREMENT_TYPE_CONFIG: Record<
  RequirementType,
  { label: string; variant: 'destructive' | 'warning' | 'secondary' }
> = {
  mandatory: { label: 'Mandatory', variant: 'destructive' },
  desirable: { label: 'Desirable', variant: 'warning' },
  information: { label: 'Information', variant: 'secondary' },
}

export const RequirementStatus = z.enum([
  'pending',
  'compliant',
  'partial',
  'non_compliant',
  'not_applicable',
])
export type RequirementStatus = z.infer<typeof RequirementStatus>

export const REQUIREMENT_STATUS_CONFIG: Record<
  RequirementStatus,
  { label: string; variant: 'secondary' | 'success' | 'warning' | 'destructive'; icon: string }
> = {
  pending: { label: 'Pending', variant: 'secondary', icon: '❓' },
  compliant: { label: 'Compliant', variant: 'success', icon: '✅' },
  partial: { label: 'Partial', variant: 'warning', icon: '⚠️' },
  non_compliant: { label: 'Non-Compliant', variant: 'destructive', icon: '❌' },
  not_applicable: { label: 'N/A', variant: 'secondary', icon: '➖' },
}

export const ExtractionSource = z.enum(['ai', 'manual'])
export type ExtractionSource = z.infer<typeof ExtractionSource>

// Artifact status (defined before RequirementSchema to avoid circular reference)
export const ArtifactStatus = z.enum(['active', 'superseded'])
export type ArtifactStatus = z.infer<typeof ArtifactStatus>

export const RequirementSchema = z.object({
  id: z.string().uuid(),
  bidId: z.string().uuid(),

  // Content
  text: z.string().min(1, 'Requirement text is required'),
  sourceRef: z.string().nullable().optional(),
  sourceDocumentId: z.string().uuid().nullable().optional(),

  // Structured source references (from extraction pipeline)
  sourcePageNumber: z.number().int().nullable().optional(),
  sourceSection: z.string().nullable().optional(),
  sourceQuote: z.string().nullable().optional(),

  // Classification
  type: RequirementType,
  category: z.string().nullable().optional(), // Free-form: "technical", "financial", custom, etc.

  // Response artefacts (what the tenderer must submit for this requirement)
  responseArtefacts: z.array(z.string()).nullable().optional(), // ['Form', 'Attachment', 'Evidence', 'Narrative', 'None']
  isSubmissionGate: z.boolean().default(false),
  isScored: z.boolean().default(false),
  deliverableName: z.string().nullable().optional(), // Clean name for Submission Tracker

  // Compliance tracking
  status: RequirementStatus,
  complianceNotes: z.string().nullable().optional(),

  // Assignment
  ownerId: z.string().uuid().nullable().optional(),
  responseSection: z.string().max(500).nullable().optional(),

  // AI metadata
  aiConfidence: z.number().min(0).max(1).nullable().optional(),
  extractionSource: ExtractionSource,
  needsReview: z.boolean().default(false),

  // Numbering - ALWAYS required (no nullable)
  requirementNumber: z.string().regex(/^REQ-\d{3,4}$/, 'Invalid requirement number format'),

  // Versioning
  artifactStatus: ArtifactStatus.default('active'),

  // Ordering
  sortOrder: z.number().int().default(0),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Requirement = z.infer<typeof RequirementSchema>

export const CreateRequirementSchema = RequirementSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  requirementNumber: true, // Generated by backend
  artifactStatus: true, // Set by backend
}).partial({
  sortOrder: true,
  status: true,
  type: true,
  extractionSource: true,
})
export type CreateRequirement = z.infer<typeof CreateRequirementSchema>

export const UpdateRequirementSchema = RequirementSchema.partial().required({ id: true })
export type UpdateRequirement = z.infer<typeof UpdateRequirementSchema>

// Category badge configuration (for display, not schema constraint)
export const DEFAULT_CATEGORY_COLORS: Record<string, string> = {
  technical: 'bg-blue-100 text-blue-800 border-blue-200',
  financial: 'bg-green-100 text-green-800 border-green-200',
  compliance: 'bg-amber-100 text-amber-800 border-amber-200',
  safety: 'bg-red-100 text-red-800 border-red-200',
  administrative: 'bg-gray-100 text-gray-800 border-gray-200',
  environmental: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  other: 'bg-slate-100 text-slate-800 border-slate-200',
}

// Helper to get category color (with fallback)
export function getCategoryColor(category: string | null | undefined): string {
  const fallback = 'bg-slate-100 text-slate-800 border-slate-200'
  if (!category) return DEFAULT_CATEGORY_COLORS['other'] || fallback
  const normalized = category.toLowerCase().trim()
  return DEFAULT_CATEGORY_COLORS[normalized] || DEFAULT_CATEGORY_COLORS['other'] || fallback
}

// Generation job status
export const GenerationJobStatus = z.enum(['pending', 'processing', 'completed', 'failed'])
export type GenerationJobStatus = z.infer<typeof GenerationJobStatus>

export const RequirementGenerationJobSchema = z.object({
  id: z.string().uuid(),
  bidId: z.string().uuid(),
  status: GenerationJobStatus,
  documentsTotal: z.number().int(),
  documentsProcessed: z.number().int(),
  requirementsFound: z.number().int(),
  errorMessage: z.string().nullable().optional(),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
})
export type RequirementGenerationJob = z.infer<typeof RequirementGenerationJobSchema>

// ===================
// Form Schemas
// ===================

export const LoginFormSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})
export type LoginForm = z.infer<typeof LoginFormSchema>

export const SignUpFormSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  fullName: z.string().min(1, 'Full name is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})
export type SignUpForm = z.infer<typeof SignUpFormSchema>

export const InviteSignUpFormSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})
export type InviteSignUpForm = z.infer<typeof InviteSignUpFormSchema>

export const ForgotPasswordFormSchema = z.object({
  email: z.string().email('Please enter a valid email'),
})
export type ForgotPasswordForm = z.infer<typeof ForgotPasswordFormSchema>

export const ResetPasswordFormSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})
export type ResetPasswordForm = z.infer<typeof ResetPasswordFormSchema>

export const ChangePasswordFormSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: 'New password must be different from current password',
  path: ['newPassword'],
})
export type ChangePasswordForm = z.infer<typeof ChangePasswordFormSchema>

export const BookDemoFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  company: z.string().min(1, 'Company name is required'),
  email: z.string().email('Valid work email is required'),
  phone: z.string().optional(),
  message: z.string().optional(),
})
export type BookDemoForm = z.infer<typeof BookDemoFormSchema>

/** Shown when a closing time is entered without a date to attach it to. */
export const DEADLINE_TIME_NEEDS_DATE = 'Choose a date for this closing time'

export const CreateBidFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  client: z.string().min(1, 'Client is required'),
  priority: BidPriority,
  value: z.union([
    z.number().positive('Value must be positive'),
    z.nan().transform(() => undefined),
  ]).optional(),
  deadline: z.string().optional(),
  /** Optional closing time for the deadline date, "HH:MM" in the person's own time zone. */
  deadlineTime: z.string().optional(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  assignedTo: z.array(z.string().uuid()).optional(),
}).refine((form) => !form.deadlineTime || !!form.deadline, {
  message: DEADLINE_TIME_NEEDS_DATE,
  path: ['deadline'],
})
export type CreateBidForm = z.infer<typeof CreateBidFormSchema>

// ===================
// API Response Schemas
// ===================

export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  /** Correlation ID for the failing request — lets users report errors with a reference. */
  requestId: z.string().optional(),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

export function createPaginatedSchema<T extends z.ZodType>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    hasMore: z.boolean(),
  })
}

// ===================
// Organization Lifecycle Schemas
// ===================

export const OrgDeletionRequestSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  requestedBy: z.string().uuid().nullable(),
  requestedByEmail: z.string().email(),
  requestedAt: z.string().datetime(),
  scheduledFor: z.string().datetime(),
  status: z.enum(['pending', 'cancelled', 'completed', 'failed']),
  cancelledAt: z.string().datetime().nullable(),
  cancelledBy: z.string().uuid().nullable(),
  cancelledByEmail: z.string().email().nullable(),
  errorMessage: z.string().nullable(),
  correlationId: z.string().nullable(),
})
export type OrgDeletionRequest = z.infer<typeof OrgDeletionRequestSchema>

export const DeletionStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('none') }),
  z.object({ status: z.literal('pending'), request: OrgDeletionRequestSchema }),
])
export type DeletionStatus = z.infer<typeof DeletionStatusSchema>

export const OrgExportJobSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  requestedBy: z.string().uuid().nullable(),
  requestedByEmail: z.string().email(),
  requestedAt: z.string().datetime(),
  status: z.enum(['pending', 'running', 'completed', 'failed']),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  estimatedSizeBytes: z.number().nullable(),
  actualSizeBytes: z.number().nullable(),
  s3Key: z.string().nullable(),
  downloadUrl: z.string().url().nullable(),
  downloadExpiresAt: z.string().datetime().nullable(),
  downloadCount: z.number(),
  lastDownloadedAt: z.string().datetime().nullable(),
  recordCounts: z.record(z.string(), z.number()).nullable(),
  errorMessage: z.string().nullable(),
  correlationId: z.string().nullable(),
})
export type OrgExportJob = z.infer<typeof OrgExportJobSchema>

export const ExportSizeEstimateSchema = z.object({
  totalBytes: z.number(),
  fileCount: z.number(),
  tableRowCounts: z.record(z.string(), z.number()),
  estimatedDurationSeconds: z.number(),
  exceedsLimit: z.boolean(),
})
export type ExportSizeEstimate = z.infer<typeof ExportSizeEstimateSchema>

export const ActiveSessionsSchema = z.object({
  activeAgentJobs: z.number(),
  activeTextractJobs: z.number(),
  totalActive: z.number(),
})
export type ActiveSessions = z.infer<typeof ActiveSessionsSchema>

/** A customer-granted window in which the BidContext team may see the AI's working. */
export const SupportAccessGrantSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  /** Null = the whole organisation. */
  bidId: z.string().nullable(),
  bidTitle: z.string().nullable(),
  grantedBy: z.string(),
  grantedByName: z.string().nullable(),
  grantedByEmail: z.string().nullable(),
  reason: z.string(),
  allowsReplay: z.boolean(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  revokedBy: z.string().nullable(),
  revokedByName: z.string().nullable(),
  createdAt: z.string(),
  status: z.enum(['active', 'expired', 'revoked']),
})
export type SupportAccessGrant = z.infer<typeof SupportAccessGrantSchema>

export interface GrantSupportAccessInput {
  days: number
  reason: string
  bidId?: string | null
  allowsReplay?: boolean
}

// ===================
// Evaluation Criteria Schemas
// ===================

export const WeightingSchema = z.object({
  percentage: z.number().min(0).max(100).optional(),
  points: z.number().optional(),
  maxPoints: z.number().optional(),
}).nullable()
export type Weighting = z.infer<typeof WeightingSchema>

export const EvaluationCriterionSchema = z.object({
  id: z.string().uuid(),
  bidId: z.string().uuid(),
  analysisRunId: z.string().uuid().nullable().optional(),

  // Content
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  sourceDocumentId: z.string().uuid().nullable().optional(),

  // Structured source references
  sourcePageNumber: z.number().int().nullable().optional(),
  sourceSection: z.string().nullable().optional(),
  sourceQuote: z.string().nullable().optional(),

  // Weighting
  weighting: WeightingSchema.optional(),
  parentId: z.string().uuid().nullable().optional(),

  // AI metadata
  contentHash: z.string().nullable().optional(),
  sourceHash: z.string().nullable().optional(),
  aiConfidence: z.number().min(0).max(1).nullable().optional(),
  needsReview: z.boolean().default(false),

  // Versioning
  artifactStatus: ArtifactStatus.default('active'),
  isActive: z.boolean().default(true),

  // Enriched from source document
  sourceDocumentKind: z.string().nullable().optional(),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type EvaluationCriterion = z.infer<typeof EvaluationCriterionSchema>

// ===================
// Submission Requirements Schemas
// ===================

export const SubmissionRequirementType = z.enum([
  'deadline',
  'lodgement_method',
  'form',
  'format',
  'page_limit',
  'attachment',
  'declaration',
  'pricing',
  'other',
])
export type SubmissionRequirementType = z.infer<typeof SubmissionRequirementType>

export const SUBMISSION_REQUIREMENT_TYPE_CONFIG: Record<
  SubmissionRequirementType,
  { label: string; variant: 'destructive' | 'warning' | 'secondary' | 'default' }
> = {
  deadline: { label: 'Deadline', variant: 'destructive' },
  lodgement_method: { label: 'Lodgement Method', variant: 'warning' },
  form: { label: 'Required Form', variant: 'secondary' },
  format: { label: 'Format', variant: 'secondary' },
  page_limit: { label: 'Page Limit', variant: 'secondary' },
  attachment: { label: 'Attachment', variant: 'secondary' },
  declaration: { label: 'Declaration', variant: 'secondary' },
  pricing: { label: 'Pricing', variant: 'warning' },
  other: { label: 'Other', variant: 'default' },
}

export const LodgementDetailsSchema = z.object({
  method: z.string().optional(),
  portal: z.string().optional(),
  url: z.string().url().optional(),
  email: z.string().email().optional(),
  address: z.string().optional(),
}).nullable()
export type LodgementDetails = z.infer<typeof LodgementDetailsSchema>

export const FormDetailsSchema = z.object({
  formName: z.string().optional(),
  formNumber: z.string().optional(),
  mandatory: z.boolean().optional(),
}).nullable()
export type FormDetails = z.infer<typeof FormDetailsSchema>

export const SubmissionRequirementSchema = z.object({
  id: z.string().uuid(),
  bidId: z.string().uuid(),
  analysisRunId: z.string().uuid().nullable().optional(),

  // Content
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  sourceDocumentId: z.string().uuid().nullable().optional(),

  // Structured source references
  sourcePageNumber: z.number().int().nullable().optional(),
  sourceSection: z.string().nullable().optional(),
  sourceQuote: z.string().nullable().optional(),

  // Classification
  requirementType: SubmissionRequirementType,

  // Type-specific fields
  deadlineDate: z.string().datetime().nullable().optional(),
  deadlineTimezone: z.string().nullable().optional(),
  lodgementDetails: LodgementDetailsSchema.optional(),
  formDetails: FormDetailsSchema.optional(),

  // AI metadata
  contentHash: z.string().nullable().optional(),
  sourceHash: z.string().nullable().optional(),
  aiConfidence: z.number().min(0).max(1).nullable().optional(),
  needsReview: z.boolean().default(false),

  // Versioning
  artifactStatus: ArtifactStatus.default('active'),
  isActive: z.boolean().default(true),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type SubmissionRequirement = z.infer<typeof SubmissionRequirementSchema>

// ===================
// Key Dates Schemas
// ===================

export const KeyDateType = z.enum(['briefing', 'site_visit', 'qa_deadline', 'intent_deadline', 'deadline', 'other'])
export type KeyDateType = z.infer<typeof KeyDateType>

export const KEY_DATE_TYPE_CONFIG: Record<
  KeyDateType,
  { label: string; variant: 'destructive' | 'warning' | 'secondary' | 'default' }
> = {
  briefing: { label: 'Briefing', variant: 'warning' },
  site_visit: { label: 'Site Visit', variant: 'warning' },
  qa_deadline: { label: 'Q&A Deadline', variant: 'destructive' },
  intent_deadline: { label: 'Intent Deadline', variant: 'destructive' },
  deadline: { label: 'Tender Closing', variant: 'destructive' },
  other: { label: 'Other', variant: 'default' },
}

export const KeyDateSchema = z.object({
  id: z.string().uuid(),
  bidId: z.string().uuid(),
  analysisRunId: z.string().uuid().nullable().optional(),

  // Content
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  sourceDocumentId: z.string().uuid().nullable().optional(),

  // Structured source references
  sourcePageNumber: z.number().int().nullable().optional(),
  sourceSection: z.string().nullable().optional(),
  sourceQuote: z.string().nullable().optional(),

  // Date info
  date: z.string().datetime(),
  dateTimezone: z.string().nullable().optional(),
  dateType: KeyDateType,

  // AI metadata
  contentHash: z.string().nullable().optional(),
  sourceHash: z.string().nullable().optional(),
  aiConfidence: z.number().min(0).max(1).nullable().optional(),
  needsReview: z.boolean().default(false),

  // Versioning
  artifactStatus: ArtifactStatus.default('active'),
  isActive: z.boolean().default(true),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type KeyDate = z.infer<typeof KeyDateSchema>

// ===================
// Compliance Items Schemas
// ===================

export const ComplianceItemStatus = z.enum(['pending', 'in_progress', 'completed', 'blocked', 'not_applicable'])
export type ComplianceItemStatus = z.infer<typeof ComplianceItemStatus>

export const COMPLIANCE_ITEM_STATUS_CONFIG: Record<
  ComplianceItemStatus,
  { label: string; variant: 'secondary' | 'warning' | 'success' | 'destructive'; icon: string }
> = {
  pending: { label: 'Pending', variant: 'secondary', icon: '⏳' },
  in_progress: { label: 'In progress', variant: 'warning', icon: '🔄' },
  completed: { label: 'Completed', variant: 'success', icon: '✅' },
  blocked: { label: 'Blocked', variant: 'destructive', icon: '🚫' },
  not_applicable: { label: 'N/A', variant: 'secondary', icon: '➖' },
}

export const ComplianceItemPriority = z.enum(['critical', 'high', 'medium', 'low'])
export type ComplianceItemPriority = z.infer<typeof ComplianceItemPriority>

export const COMPLIANCE_ITEM_PRIORITY_CONFIG: Record<
  ComplianceItemPriority,
  { label: string; variant: 'priority-critical' | 'priority-high' | 'priority-medium' | 'priority-low' }
> = {
  critical: { label: 'Critical', variant: 'priority-critical' },
  high: { label: 'High', variant: 'priority-high' },
  medium: { label: 'Medium', variant: 'priority-medium' },
  low: { label: 'Low', variant: 'priority-low' },
}

export const ComplianceItemCategory = z.enum(['go_no_go', 'blocking', 'critical', 'standard'])
export type ComplianceItemCategory = z.infer<typeof ComplianceItemCategory>

export const COMPLIANCE_ITEM_CATEGORY_CONFIG: Record<
  ComplianceItemCategory,
  { label: string; variant: 'destructive' | 'warning' | 'secondary' }
> = {
  go_no_go: { label: 'Go/No-Go', variant: 'destructive' },
  blocking: { label: 'Blocking', variant: 'warning' },
  critical: { label: 'Critical', variant: 'warning' },
  standard: { label: 'Standard', variant: 'secondary' },
}

// Normalize backend category format to frontend enum
// Backend returns: 'Go/No-Go', 'Blocking', 'Critical', 'Standard'
// Frontend expects: 'go_no_go', 'blocking', 'critical', 'standard'
export function normalizeComplianceCategory(category: string | null | undefined): ComplianceItemCategory {
  if (!category) return 'standard'
  const normalized = category.toLowerCase().replace(/[^a-z]/g, '_').replace(/_+/g, '_')
  if (normalized === 'go_no_go') return 'go_no_go'
  if (normalized === 'blocking') return 'blocking'
  if (normalized === 'critical') return 'critical'
  if (normalized === 'standard') return 'standard'
  return 'standard' // Fallback for unknown categories
}

export const ComplianceSourceType = z.enum([
  'requirement',
  'evaluation_criterion',
  'submission_requirement',
  'key_date',
])
export type ComplianceSourceType = z.infer<typeof ComplianceSourceType>

// Phase classification enums
export const DeliverableType = z.enum(['Form', 'Attachment', 'Evidence', 'Narrative', 'None'])
export type DeliverableType = z.infer<typeof DeliverableType>

export const DELIVERABLE_TYPE_CONFIG: Record<
  DeliverableType,
  { label: string; variant: 'secondary' | 'default' }
> = {
  Form: { label: 'Form', variant: 'secondary' },
  Attachment: { label: 'Attachment', variant: 'secondary' },
  Evidence: { label: 'Evidence', variant: 'secondary' },
  Narrative: { label: 'Narrative', variant: 'default' },
  None: { label: 'None', variant: 'default' },
}

export const CompliancePhase = z.enum(['pre_submission', 'post_award', 'during_works', 'unknown'])
export type CompliancePhase = z.infer<typeof CompliancePhase>

export const COMPLIANCE_PHASE_CONFIG: Record<
  CompliancePhase,
  { label: string; variant: 'default' | 'secondary' | 'warning' }
> = {
  pre_submission: { label: 'Pre-Submission', variant: 'default' },
  post_award: { label: 'Post-Award', variant: 'secondary' },
  during_works: { label: 'During Works', variant: 'secondary' },
  unknown: { label: 'Unknown', variant: 'warning' },
}

export const PhaseConfidence = z.enum(['high', 'medium', 'low'])
export type PhaseConfidence = z.infer<typeof PhaseConfidence>

export const ComplianceItemSchema = z.object({
  id: z.string().uuid(),
  bidId: z.string().uuid(),
  analysisRunId: z.string().uuid().nullable().optional(),

  // Content
  title: z.string().min(1),
  description: z.string().nullable().optional(),

  // Source link (polymorphic)
  sourceType: ComplianceSourceType,
  sourceRequirementId: z.string().uuid().nullable().optional(),
  sourceEvalCriterionId: z.string().uuid().nullable().optional(),
  sourceSubmissionReqId: z.string().uuid().nullable().optional(),
  sourceKeyDateId: z.string().uuid().nullable().optional(),

  // Classification
  category: ComplianceItemCategory,
  priority: ComplianceItemPriority,

  // Tracking
  status: ComplianceItemStatus,
  ownerId: z.string().uuid().nullable().optional(),
  dueDate: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional(),

  // AI metadata
  aiConfidence: z.number().min(0).max(1).nullable().optional(),
  needsReview: z.boolean().default(false),

  // Phase classification fields
  deliverableType: DeliverableType.nullable().optional(),
  phase: CompliancePhase.default('pre_submission'),
  phaseConfidence: PhaseConfidence.default('medium'),
  phaseSignals: z.array(z.string()).default([]),
  phaseEvidenceQuote: z.string().nullable().optional(),
  phaseOverrideBy: z.string().uuid().nullable().optional(),
  phaseClassificationVersion: z.string().default('v1'),

  // Source document tracking
  sourceDocumentId: z.string().uuid().nullable().optional(),
  sourceDocumentName: z.string().nullable().optional(),

  // Versioning
  artifactStatus: ArtifactStatus.default('active'),
  isActive: z.boolean().default(true),

  // Stable per-bid requirement number (mig 194), null until the row is active.
  // Optional so responses from before the migration still parse.
  displayNumber: z.number().int().nullable().optional(),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type ComplianceItem = z.infer<typeof ComplianceItemSchema>

// Compliance Item Source (for navigation)
export const ComplianceItemSourceSchema = z.object({
  sourceType: ComplianceSourceType,
  sourceId: z.string().uuid(),
  sourceTitle: z.string(),
  sourceDescription: z.string().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  sourcePageNumber: z.number().int().nullable().optional(),
  sourceSection: z.string().nullable().optional(),
  tabRoute: z.string(), // e.g., '/bids/{bidId}/requirements?highlight={sourceId}'
})
export type ComplianceItemSource = z.infer<typeof ComplianceItemSourceSchema>

// ===================
// Requirement-Criteria Links Schemas
// ===================

export const LinkSource = z.enum(['ai', 'manual'])
export type LinkSource = z.infer<typeof LinkSource>

export const RequirementCriteriaLinkSchema = z.object({
  id: z.string().uuid(),
  requirementId: z.string().uuid(),
  evaluationCriterionId: z.string().uuid(),
  linkSource: LinkSource.default('ai'),
  aiConfidence: z.number().min(0).max(1).nullable().optional(),
  createdAt: z.string().datetime(),
})
export type RequirementCriteriaLink = z.infer<typeof RequirementCriteriaLinkSchema>

// ===================
// Coverage Stats Schemas
// ===================

export const RequirementsCoverageStatsSchema = z.object({
  total: z.number().int(),
  linkedToCriteria: z.number().int(),
  unlinkedToCriteria: z.number().int(),
  byType: z.object({
    mandatory: z.object({ total: z.number().int(), linked: z.number().int() }),
    desirable: z.object({ total: z.number().int(), linked: z.number().int() }),
    information: z.object({ total: z.number().int(), linked: z.number().int() }),
  }),
  criteriaWithMostRequirements: z.array(z.object({
    criterionId: z.string().uuid(),
    criterionTitle: z.string(),
    requirementCount: z.number().int(),
  })),
  criteriaWithNoRequirements: z.array(z.object({
    criterionId: z.string().uuid(),
    criterionTitle: z.string(),
    weighting: z.number().nullable(),
  })),
})
export type RequirementsCoverageStats = z.infer<typeof RequirementsCoverageStatsSchema>

export const EvaluationCoverageStatsSchema = z.object({
  total: z.number().int(),
  withRequirements: z.number().int(),
  withoutRequirements: z.number().int(),
  coveragePercent: z.number(),
  weightedCoveragePercent: z.number(),
  uncoveredHighWeighting: z.array(z.object({
    criterionId: z.string().uuid(),
    criterionTitle: z.string(),
    weighting: z.number(),
  })),
})
export type EvaluationCoverageStats = z.infer<typeof EvaluationCoverageStatsSchema>

// ===================
// Bid Overview Schema (Enhanced — cockpit view, pre-submission only)
// ===================

export const UpcomingDeadlineSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  date: z.string(),
  source: z.enum(['submission_requirement', 'key_date']),
  severity: z.enum(['overdue', 'due_soon', 'upcoming']),
  daysUntil: z.number().int(),
})
export type UpcomingDeadline = z.infer<typeof UpcomingDeadlineSchema>

export const OverviewRiskFlagSchema = z.object({
  severity: z.enum(['critical', 'warning', 'info']),
  message: z.string(),
  resolveTab: z.string(),
  resolveFilters: z.record(z.string(), z.string()),
})
export type OverviewRiskFlag = z.infer<typeof OverviewRiskFlagSchema>

export const TopSourceDocumentSchema = z.object({
  documentId: z.string().uuid(),
  documentName: z.string(),
  openItems: z.number().int(),
  criticalOpen: z.number().int(),
  highOpen: z.number().int(),
})
export type TopSourceDocument = z.infer<typeof TopSourceDocumentSchema>

export const BidOverviewSchema = z.object({
  // Key deadlines
  submissionDeadline: SubmissionRequirementSchema.nullable(),
  upcomingDeadlines: z.array(UpcomingDeadlineSchema),

  // Submission info
  lodgementMethod: SubmissionRequirementSchema.nullable(),
  requiredForms: z.array(SubmissionRequirementSchema),
  formsTotal: z.number().int(),
  formsCompleted: z.number().int(),

  // Submission readiness (replaces complianceStats)
  submissionReadiness: z.object({
    percentComplete: z.number().int(),
    completed: z.number().int(),
    total: z.number().int(),
    criticalPending: z.number().int(),
    highPending: z.number().int(),
    overdueCount: z.number().int(),
    inProgress: z.number().int(),
    blocked: z.number().int(),
    unassignedCritical: z.number().int(),
  }),

  // Requirements summary (replaces requirementsStats)
  requirementsSummary: z.object({
    total: z.number().int(),
    mandatory: z.number().int(),
    desirable: z.number().int(),
    information: z.number().int(),
    linkedToCriteria: z.number().int(),
    unlinkedToCriteria: z.number().int(),
  }),

  // Evaluation summary (replaces evaluationStats)
  evaluationSummary: z.object({
    total: z.number().int(),
    totalWeighting: z.number(),
    withRequirements: z.number().int(),
    withoutRequirements: z.number().int(),
  }),

  // Deliverable breakdown
  deliverableBreakdown: z.record(z.string(), z.number()),

  // Top source documents needing attention
  topSourceDocuments: z.array(TopSourceDocumentSchema),

  // Top risk flags (embedded, no separate call needed)
  topRiskFlags: z.array(OverviewRiskFlagSchema),
})
export type BidOverview = z.infer<typeof BidOverviewSchema>

// ===================
// Settings Schemas
// ===================

export const OrgProfileSchema = z.object({
  companyName: z.string().max(255).optional(),
  tradingAs: z.string().max(255).optional(),
  abn: z.string().max(20).optional(),
  primaryServices: z.array(z.string().max(100)).max(20).optional(),
  industries: z.array(z.string().max(100)).max(10).optional(),
  geographicCoverage: z.array(z.string().max(100)).max(20).optional(),
  certifications: z.array(z.object({
    name: z.string().max(200),
    expiryDate: z.string().optional(),
  })).max(30).optional(),
  companyAge: z.string().max(50).optional(),
  employeeCount: z.string().max(50).optional(),
  annualRevenue: z.string().max(50).optional(),
  projectsCompleted: z.string().max(50).optional(),
  differentiators: z.array(z.string().max(500)).max(10).optional(),
  safetyRecord: z.object({
    ltifr: z.string().max(20).optional(),
    trifr: z.string().max(20).optional(),
    safetyMilestones: z.array(z.string().max(200)).max(10).optional(),
  }).optional(),
  // Schedule-fill inputs: the entity, insurance and referee facts tender
  // schedules ask for verbatim.
  entityDetails: z.object({
    acn: z.string().max(20).optional(),
    registeredAddress: z.string().max(500).optional(),
    postalAddress: z.string().max(500).optional(),
    primaryContactName: z.string().max(200).optional(),
    primaryContactEmail: z.string().max(200).optional(),
    primaryContactPhone: z.string().max(50).optional(),
  }).optional(),
  insurances: z.array(z.object({
    type: z.string().max(200),
    insurer: z.string().max(200).optional(),
    policyNumber: z.string().max(100).optional(),
    coverageAmount: z.string().max(50).optional(),
    expiryDate: z.string().max(50).optional(),
  })).max(20).optional(),
  referees: z.array(z.object({
    name: z.string().max(200),
    organisation: z.string().max(200).optional(),
    role: z.string().max(200).optional(),
    phone: z.string().max(50).optional(),
    email: z.string().max(200).optional(),
    relatedProject: z.string().max(200).optional(),
  })).max(20).optional(),
})
export type OrgProfile = z.infer<typeof OrgProfileSchema>

export const WritingPreferencesSchema = z.object({
  personPerspective: z.enum(['first_plural', 'third_person', 'company_name']).optional(),
  useNumberedHeadings: z.boolean().optional(),
  companyReferenceStyle: z.string().max(100).optional(),
  teamTerminology: z.enum(['team', 'personnel', 'staff', 'workforce']).optional(),
  /** Rides the preferences form; stored on its own org column server-side. */
  autoHarvestEnabled: z.boolean().optional(),
})
export type WritingPreferences = z.infer<typeof WritingPreferencesSchema>

export const OrgSettingsSchema = z.object({
  id: z.string(),
  name: z.string(),
  profile: OrgProfileSchema,
  writingPreferences: WritingPreferencesSchema,
  // Submission auto-harvest switch (Specs/win-loss-harvest-spec.md §1.2)
  autoHarvestEnabled: z.boolean().optional(),
})
export type OrgSettings = z.infer<typeof OrgSettingsSchema>

// ===================
// Review Agent Schemas
// ===================

export const ReviewSeveritySchema = z.enum(['high', 'medium', 'low'])
export type ReviewSeverity = z.infer<typeof ReviewSeveritySchema>

/** Sentence-case severity words for Checks cards ("High · Clarity"). */
export const REVIEW_SEVERITY_LABEL: Record<ReviewSeverity, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const ReviewCriterionScopeSchema = z.enum(['system', 'user'])

export const ReviewCriterionSchema = z.object({
  id: z.string(),
  scope: ReviewCriterionScopeSchema,
  organizationId: z.string().nullable(),
  createdBy: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  prompt: z.string(),
  systemPromptKey: z.string().nullable(),
  // Which run scope the criterion is meaningful against. Defaulted so a
  // response from a backend that predates the applies_to column still parses.
  appliesTo: z.enum(['section', 'artifact', 'both']).default('both'),
  sortOrder: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type ReviewCriterion = z.infer<typeof ReviewCriterionSchema>
export type ReviewCriterionAppliesTo = ReviewCriterion['appliesTo']

/**
 * Mirrors the server-side gate in `backend/src/lib/review-criterion-scope.ts`.
 * A run at `runScope` may only send criteria that apply to it — otherwise
 * `POST /review-runs` rejects it with 400 CRITERION_SCOPE_MISMATCH.
 *
 * Tolerates a missing value (older payloads, hand-built fixtures) as 'both'.
 */
export function criterionAppliesToScope(
  criterion: { appliesTo?: ReviewCriterionAppliesTo },
  runScope: 'section' | 'artifact',
): boolean {
  const appliesTo = criterion.appliesTo ?? 'both'
  return appliesTo === 'both' || appliesTo === runScope
}

/**
 * Which of the currently-ticked criteria survive a switch to `runScope`.
 *
 * Extracted from the ReviewTab handler so it can be tested without rendering
 * the whole panel. An id whose criterion is not in `known` is KEPT: the picker
 * may not have loaded yet, and dropping a selection because we cannot see its
 * definition would be a silent, unexplainable loss.
 */
export function keepCriteriaForScope(
  selectedIds: string[],
  known: { id: string; appliesTo?: ReviewCriterionAppliesTo }[],
  runScope: 'section' | 'artifact',
): string[] {
  return selectedIds.filter((id) => {
    const criterion = known.find((c) => c.id === id)
    return !criterion || criterionAppliesToScope(criterion, runScope)
  })
}

export const ReviewCriteriaListSchema = z.object({
  system: z.array(ReviewCriterionSchema),
  mine: z.array(ReviewCriterionSchema),
})
export type ReviewCriteriaList = z.infer<typeof ReviewCriteriaListSchema>

export const CreateReviewCriterionInputSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  prompt: z.string().min(1).max(2000),
})
export type CreateReviewCriterionInput = z.infer<typeof CreateReviewCriterionInputSchema>

export const UpdateReviewCriterionInputSchema = CreateReviewCriterionInputSchema.partial()
export type UpdateReviewCriterionInput = z.infer<typeof UpdateReviewCriterionInputSchema>

/**
 * How a finding can be acted on. `rewritable` findings can be handed to the
 * AI to fix; `human_input` findings need a person (a missing price, a decision
 * nobody has made yet) and only offer the comment / dismiss routes.
 */
export const ReviewActionClassSchema = z.enum(['rewritable', 'human_input'])
export type ReviewActionClass = z.infer<typeof ReviewActionClassSchema>

export const ReviewFindingSchema = z.object({
  criterionId: z.string(),
  criterionName: z.string(),
  severity: ReviewSeveritySchema,
  message: z.string(),
  excerpt: z.string().nullable(),
  sectionId: z.string().nullable(),
  promptVersion: z.string(),
  /**
   * Stable identity for the underlying issue (32 hex chars), independent of
   * which run produced it. Optional on a raw run's findings — older runs
   * predate the key.
   */
  issueKey: z.string().optional(),
  actionClass: ReviewActionClassSchema.optional(),
})
export type ReviewFinding = z.infer<typeof ReviewFindingSchema>

export const ReviewRunStatusSchema = z.enum(['running', 'complete', 'failed'])
export type ReviewRunStatus = z.infer<typeof ReviewRunStatusSchema>

export const ReviewRunSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  bidId: z.string(),
  artifactId: z.string(),
  sectionKey: z.string().nullable(),
  userId: z.string(),
  criteriaIds: z.array(z.string()),
  status: ReviewRunStatusSchema,
  findings: z.array(ReviewFindingSchema),
  errorMessage: z.string().nullable(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  dismissedAt: z.string().nullable(),
})
export type ReviewRun = z.infer<typeof ReviewRunSchema>

export const ReviewRunSummarySchema = z.object({
  id: z.string(),
  artifactId: z.string(),
  sectionKey: z.string().nullable(),
  criteriaIds: z.array(z.string()),
  status: ReviewRunStatusSchema,
  findingCount: z.number(),
  severityCounts: z.object({
    high: z.number(),
    medium: z.number(),
    low: z.number(),
  }),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  dismissedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  /** Who started the run. Null for runs recorded before the column existed. */
  userId: z.string().nullable(),
})
export type ReviewRunSummary = z.infer<typeof ReviewRunSummarySchema>

/**
 * Lifecycle of one issue against one artifact. `commented` means the issue was
 * promoted to a comment thread — the fix now lives with a person, not the AI.
 */
export const IssueActionStatusSchema = z.enum([
  'open', 'addressing', 'applied', 'dismissed', 'failed', 'commented',
])
export type IssueActionStatus = z.infer<typeof IssueActionStatusSchema>

export const MergedReviewFindingSchema = ReviewFindingSchema.extend({
  /** Always present on the merged view — the server derives it for every finding. */
  issueKey: z.string(),
  runId: z.string(),
  findingIndex: z.number(),
  completedAt: z.string(),
  actionStatus: IssueActionStatusSchema,
  commentId: z.string().nullable(),
  dismissReason: z.string().nullable(),
})
export type MergedReviewFinding = z.infer<typeof MergedReviewFindingSchema>

/** Server-side state for one issue on one artifact. */
export const ReviewIssueStateSchema = z.object({
  id: z.string(),
  artifactId: z.string(),
  issueKey: z.string(),
  status: z.enum(['addressing', 'applied', 'dismissed', 'failed', 'commented']),
  userId: z.string().nullable(),
  agentThreadId: z.string().nullable(),
  commentId: z.string().nullable(),
  dismissReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
})
export type ReviewIssueState = z.infer<typeof ReviewIssueStateSchema>

/**
 * The latest check run for an artifact, plus whether the draft has moved on
 * since it finished (`stale`).
 */
export const LatestCheckSchema = z.object({
  run: z.object({
    id: z.string(),
    status: ReviewRunStatusSchema,
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    errorMessage: z.string().nullable(),
    findingCount: z.number(),
    criteriaIds: z.array(z.string()),
    userId: z.string().nullable(),
  }).nullable(),
  stale: z.boolean(),
  latestContentUpdate: z.string().nullable(),
})
export type LatestCheck = z.infer<typeof LatestCheckSchema>

export const ReviewPermissionsSchema = z.object({
  canArtifactScope: z.boolean(),
  canSectionScope: z.boolean(),
  canFixAll: z.boolean(),
  fixableSections: z.array(z.string()),
})
export type ReviewPermissions = z.infer<typeof ReviewPermissionsSchema>

export const StartReviewRunInputSchema = z.object({
  bidId: z.string(),
  artifactId: z.string(),
  sectionKey: z.string().nullable().optional(),
  criteriaIds: z.array(z.string()).min(1).max(10),
})
export type StartReviewRunInput = z.infer<typeof StartReviewRunInputSchema>

// ===================
// Addendum Reconciliation (PR 3)
//
// A reconciliation run compares an uploaded addendum against the existing
// analysis and proposes per-category changes. The Documents tab surfaces the
// run status; PR 4 ships the review surface.
// ===================

export const ReconciliationRunStatusSchema = z.enum([
  'queued',
  'running',
  'review',
  'applied',
  'dismissed',
  'failed',
])
export type ReconciliationRunStatus = z.infer<typeof ReconciliationRunStatusSchema>

export const ReconciliationDecisionCountsSchema = z.object({
  pending: z.number(),
  accepted: z.number(),
  dismissed: z.number(),
})
export type ReconciliationDecisionCounts = z.infer<
  typeof ReconciliationDecisionCountsSchema
>

export const ReconciliationDeadlineChangeSchema = z.object({
  event: z.string(),
  oldDateIso: z.string().nullable(),
  proposedDateIso: z.string().nullable(),
  label: z.string().nullable(),
})

export const ReconciliationHighlightsSchema = z
  .object({
    deadlineChange: ReconciliationDeadlineChangeSchema.optional(),
  })
  .nullable()
  .optional()

/** Enriched run row from the runs-list endpoint (adds decisionCounts + highlights). */
export const ReconciliationRunSummarySchema = z.object({
  id: z.string(),
  bidId: z.string(),
  documentId: z.string(),
  status: ReconciliationRunStatusSchema,
  counts: z.record(z.string(), z.unknown()).nullable().optional(),
  error: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable().optional(),
  decisionCounts: ReconciliationDecisionCountsSchema,
  highlights: ReconciliationHighlightsSchema,
})
export type ReconciliationRunSummary = z.infer<typeof ReconciliationRunSummarySchema>

/** Bare run row (poll endpoint). No decisionCounts/highlights — those come from the list. */
export const ReconciliationRunSchema = z.object({
  id: z.string(),
  bidId: z.string(),
  documentId: z.string(),
  status: ReconciliationRunStatusSchema,
  counts: z.record(z.string(), z.unknown()).nullable().optional(),
  error: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable().optional(),
})
export type ReconciliationRun = z.infer<typeof ReconciliationRunSchema>

export const ReconciliationRunsResponseSchema = z.object({
  runs: z.array(ReconciliationRunSummarySchema),
})

export const StartReconciliationResponseSchema = z.object({
  runId: z.string(),
  status: ReconciliationRunStatusSchema,
})

export const ReconciliationProposalKindSchema = z.enum(['add', 'change', 'remove'])
export type ReconciliationProposalKind = z.infer<typeof ReconciliationProposalKindSchema>

export const ReconciliationProposalStatusSchema = z.enum([
  'pending',
  'accepted',
  'dismissed',
])
export type ReconciliationProposalStatus = z.infer<
  typeof ReconciliationProposalStatusSchema
>

/** Live target snapshot joined onto change/remove proposals (null ⇒ orphaned). */
export const ReconciliationTargetViewSchema = z.object({
  currentValues: z.record(z.string(), z.unknown()),
  userTouched: z.boolean(),
  status: z.string().nullable(),
  updatedAt: z.string(),
  isSubmissionDeadline: z.boolean(),
})
export type ReconciliationTargetView = z.infer<typeof ReconciliationTargetViewSchema>

/**
 * A single proposed change joined with its live target. `payload` keys are the
 * snake_case DB-column names of the target item's fields (see PR 3); `evidence`
 * keys are camelCase (`quote` / `pageNumber` / `highlightData`). `target` is null
 * for `add` proposals and for orphaned change/remove targets.
 */
export const ReconciliationProposalSchema = z.object({
  id: z.string(),
  category: z.string(),
  kind: ReconciliationProposalKindSchema,
  targetItemId: z.string().nullable().optional(),
  targetVersion: z.string().nullable().optional(),
  payload: z.record(z.string(), z.unknown()).nullable().optional(),
  evidence: z.record(z.string(), z.unknown()).nullable().optional(),
  status: ReconciliationProposalStatusSchema,
  sourceDocumentId: z.string().nullable().optional(),
  sourceDocumentLabel: z.string().nullable().optional(),
  createdAt: z.string(),
  decidedAt: z.string().nullable().optional(),
  decidedBy: z.string().nullable().optional(),
  target: ReconciliationTargetViewSchema.nullable().optional(),
})
export type ReconciliationProposal = z.infer<typeof ReconciliationProposalSchema>

export const ReconciliationRunDetailResponseSchema = z.object({
  run: ReconciliationRunSchema,
  proposals: z.array(ReconciliationProposalSchema).optional().default([]),
})
export type ReconciliationRunDetailResponse = z.infer<
  typeof ReconciliationRunDetailResponseSchema
>

// ===================
// Section Flags (PR 5 — addendum "affected section" markers)
//
// A section flag records that an accepted addendum change touched a requirement
// mapped to a response section, so the section can be re-reviewed. The backend
// rolls up per (artifact, sectionKey, reconciliationRunId); the frontend merges
// further per (artifact, sectionKey) at read time — see mergeFlagsBySection.
// ===================

export const SectionFlagSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  bidId: z.string(),
  artifactId: z.string(),
  sectionKey: z.string(),
  reason: z.string(),
  sourceDocumentId: z.string().nullable().optional(),
  sourceLabel: z.string().nullable().optional(),
  reconciliationRunId: z.string().nullable().optional(),
  itemCount: z.number(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable().optional(),
  resolvedBy: z.string().nullable().optional(),
})
export type SectionFlag = z.infer<typeof SectionFlagSchema>

export const SectionFlagsResponseSchema = z.object({
  flags: z.array(SectionFlagSchema),
})

export const SectionFlagResolveResponseSchema = z.object({
  flag: SectionFlagSchema,
})

/** True when a document has been flagged (by detection or the user) as an addendum. */
export function isAddendumDocument(doc: Pick<Document, 'metadata'>): boolean {
  return doc.metadata?.['documentRole'] === 'addendum-clarification'
}

/**
 * Where the backend's `metadata.documentRoleSource` says a role came from:
 * the file name, first-page keywords, the tender metadata model, or a person.
 */
export type DocumentRoleSource = 'name' | 'content' | 'model' | 'user'

/**
 * 'user' when a human confirmed the addendum role; 'detected' for every
 * automatic source — name, content keywords and the model pick alike. A
 * model-picked role is a detection the user has not confirmed, never a choice.
 */
export function addendumRoleSource(doc: Pick<Document, 'metadata'>): 'user' | 'detected' {
  const source = doc.metadata?.['documentRoleSource'] as DocumentRoleSource | undefined
  return source === 'user' ? 'user' : 'detected'
}

/**
 * True when this addendum arrived before the bid had ever been analysed, so it
 * is part of the baseline corpus rather than a change to it.
 *
 * Stamped by the backend at classification time. Reconciliation compares an
 * addendum against an existing analysis, so a baseline addendum has nothing to
 * be compared with — every entry point into the review flow is hidden for it,
 * and it is simply read as another source document by the first analysis.
 *
 * Absent on documents classified before the flag shipped, which reads as
 * `false` and preserves their existing "Review changes" prompt.
 */
export function isBaselineAddendum(doc: Pick<Document, 'metadata'>): boolean {
  return doc.metadata?.['addendumBaseline'] === true
}

/** True when this addendum still needs reconciling against the bid's analysis. */
export function addendumNeedsReconciliation(doc: Pick<Document, 'metadata'>): boolean {
  return isAddendumDocument(doc) && !isBaselineAddendum(doc)
}
